import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Database, Download, Upload, FileSpreadsheet, CheckCircle2, AlertTriangle,
  XCircle, Copy, RefreshCw, ArrowRight,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import apiClient from '../../lib/apiClient';
import ToastMsg from '../../components/common/ToastMsg';
import { cn } from '../../utils/tailwindUtils';


const TEMPLATE_URL = 'engage/imports/template/';
const UPLOAD_URL   = 'engage/imports/upload/';

const RESULT_BADGE = {
  ready:     { label: 'Ready',     icon: CheckCircle2,  cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  imported:  { label: 'Imported',  icon: CheckCircle2,  cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  duplicate: { label: 'Duplicate', icon: Copy,          cls: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300' },
  error:     { label: 'Error',     icon: XCircle,       cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
};

const FILTERS = [
  { value: 'all',       label: 'All' },
  { value: 'ok',        label: 'Ready / imported' },
  { value: 'duplicate', label: 'Duplicates' },
  { value: 'error',     label: 'Errors' },
  { value: 'warning',   label: 'With warnings' },
];


/**
 * Engage → Bulk Import. Download the tenant's template, upload it, review a
 * dry run of every row, then import. The server re-checks everything on the
 * real import, so a file edited between the two steps cannot slip through.
 */
export default function EnquiryBulkImport() {
  const navigate = useNavigate();
  const { create } = useApiMethods();
  const fileRef = useRef(null);

  const [file, setFile] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);   // last server response
  const [filter, setFilter] = useState('all');

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const data = await apiClient.get(TEMPLATE_URL, { responseType: 'blob', timeout: 60000 });
      const blob = data instanceof Blob ? data : new Blob([data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'enquiry_import_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error('Template download failed', e);
      ToastMsg('error', 'Failed to download template');
    } finally {
      setDownloading(false);
    }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    if (!/\.xlsx$/i.test(f.name)) {
      ToastMsg('error', 'Please choose an .xlsx file');
      return;
    }
    setFile(f);
    setResult(null);
    setFilter('all');
  };

  const send = async (commit) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (commit) fd.append('commit', '1');
      const res = await create(UPLOAD_URL, fd, true);
      if (res?.status === 'success') {
        setResult(res);
        setFilter('all');
        ToastMsg('success', res.message);
      } else {
        setResult(null);
        ToastMsg('error', res?.message || 'Upload failed');
      }
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setFile(null);
    setResult(null);
    setFilter('all');
  };

  const counts = result?.counts || {};
  const rows = (result?.rows || []).filter(r => {
    if (filter === 'ok') return r.result === 'ready' || r.result === 'imported';
    if (filter === 'warning') return r.warnings?.length > 0;
    if (filter !== 'all') return r.result === filter;
    return true;
  });
  const committed = !!result?.committed;

  return (
    <div className="p-4 max-w-[1400px] mx-auto space-y-4">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center text-white shadow-sm">
          <Database className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">Bulk Import</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Add many patient enquiries at once from an Excel sheet.
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Step 1 */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-primary">Step 1</div>
          <h2 className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">Download the template</h2>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            It lists your own sources, patient types, stages and staff, so the names you type will match.
            Name and mobile are required; everything else is optional.
          </p>
          <button onClick={downloadTemplate} disabled={downloading}
            className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
            <Download className="w-3.5 h-3.5" /> {downloading ? 'Preparing…' : 'Download template (.xlsx)'}
          </button>
        </div>

        {/* Step 2 */}
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-primary">Step 2</div>
          <h2 className="mt-1 text-sm font-semibold text-gray-900 dark:text-white">Upload and check</h2>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Every row is checked first; nothing is saved until you press Import.
            Mobiles already in the CRM are skipped, never overwritten.
          </p>
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={onPick} />
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button onClick={() => fileRef.current?.click()} disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
              <FileSpreadsheet className="w-3.5 h-3.5" /> {file ? 'Choose another file' : 'Choose file'}
            </button>
            {file && (
              <span className="text-xs text-gray-600 dark:text-gray-300 truncate max-w-[220px]" title={file.name}>
                {file.name}
              </span>
            )}
            {file && !committed && (
              <button onClick={() => send(false)} disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-sm hover:opacity-90 disabled:opacity-50">
                <Upload className="w-3.5 h-3.5" /> {busy ? 'Checking…' : result ? 'Check again' : 'Check file'}
              </button>
            )}
          </div>
        </div>
      </div>

      {result && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                {committed ? 'Import finished' : 'Check result'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{result.message}</div>
            </div>
            <div className="flex items-center gap-2">
              {!committed && counts.ready > 0 && (
                <button onClick={() => send(true)} disabled={busy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90 disabled:opacity-50">
                  <Upload className="w-3.5 h-3.5" />
                  {busy ? 'Importing…' : `Import ${counts.ready} ${counts.ready === 1 ? 'enquiry' : 'enquiries'}`}
                </button>
              )}
              {committed && counts.imported > 0 && (
                <button onClick={() => navigate('/engage/enquiries')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90">
                  View enquiries <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
              {committed && (
                <button onClick={reset}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                  <RefreshCw className="w-3.5 h-3.5" /> Import another file
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 px-4 py-3">
            {FILTERS.map(f => {
              const n = f.value === 'all' ? result.rows.length
                : f.value === 'ok' ? (counts.ready || 0) + (counts.imported || 0)
                : f.value === 'warning' ? result.rows.filter(r => r.warnings?.length).length
                : counts[f.value] || 0;
              return (
                <button key={f.value} onClick={() => setFilter(f.value)}
                  className={cn(
                    'px-3 py-1 rounded-full text-xs font-medium border transition-colors',
                    filter === f.value
                      ? 'bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50',
                  )}>
                  {f.label} <span className="ml-1 opacity-70">{n}</span>
                </button>
              );
            })}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40 border-y border-gray-100 dark:border-gray-700">
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <th className="px-4 py-2.5 font-medium">Row</th>
                  <th className="px-4 py-2.5 font-medium">Result</th>
                  <th className="px-4 py-2.5 font-medium">Patient</th>
                  <th className="px-4 py-2.5 font-medium">Mobile</th>
                  <th className="px-4 py-2.5 font-medium">Patient type / stage</th>
                  <th className="px-4 py-2.5 font-medium">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No rows in this filter.</td></tr>
                ) : rows.map(r => {
                  const badge = RESULT_BADGE[r.result] || RESULT_BADGE.error;
                  const Icon = badge.icon;
                  return (
                    <tr key={r.row}
                      onClick={r.id ? () => navigate(`/engage/enquiries/${r.id}`) : undefined}
                      className={cn(r.id && 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40')}>
                      <td className="px-4 py-2.5 text-xs text-gray-500 tabular-nums">{r.row}</td>
                      <td className="px-4 py-2.5">
                        <span className={cn('inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium', badge.cls)}>
                          <Icon className="w-3 h-3" /> {badge.label}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-white">{r.name || '—'}</td>
                      <td className="px-4 py-2.5 text-[12px] text-gray-700 dark:text-gray-300">{r.mobile || '—'}</td>
                      <td className="px-4 py-2.5 text-[12px] text-gray-700 dark:text-gray-300">
                        {r.patient_type ? `${r.patient_type}${r.stage ? ` · ${r.stage}` : ''}` : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-[12px]">
                        {r.message && (
                          <div className={r.result === 'error' ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}>{r.message}</div>
                        )}
                        {r.warnings?.map((w, i) => (
                          <div key={i} className="flex items-start gap-1 text-amber-700 dark:text-amber-400">
                            <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> {w}
                          </div>
                        ))}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
